/**
 * THE LAUNCH SEQUENCE — what an installed shell shows before the app itself.
 *
 * WHY THIS MODULE EXISTS SEPARATELY FROM `app-shell.ts`. That module answers
 * *which shell* is rendering us; this one answers *what the first two seconds
 * of that shell look like*. They are two questions with two lifetimes: the
 * shell verdict is a property of the document, the launch sequence is an event
 * that happens once per document and then never again. Keeping the constants
 * and the storage contract here means the components can stay presentational
 * and the rules stay unit-testable with no DOM.
 *
 * THE SEQUENCE, AND WHERE EACH PIECE ACTUALLY LIVES:
 *
 *   native splash   Android draws `iconUrl` on `backgroundColor` (Bubblewrap,
 *                   `android/twa-manifest.json`); iOS draws its launch storyboard.
 *                   NEITHER IS OURS TO STYLE from this repo — changing them
 *                   means a new store binary. Both hand off on `#090A0D`.
 *   web splash      `AppSplashScreen`. Continues that exact colour, so the
 *                   native splash appears to *become* it rather than cut to it,
 *                   and adds the motion the native one cannot have.
 *   intro           `AppIntroCarousel`. First launch after install only.
 *   the app         Everything else.
 *
 * =========================================================================
 * WHY THE SPLASH IS CSS-DRIVEN AND NOT STATE-DRIVEN
 * =========================================================================
 *
 * It must satisfy two constraints that rule out `useState` + a timer:
 *
 *  1. IT HAS TO PAINT BEFORE HYDRATION. A splash that mounts when React does
 *     is not a splash — the user sees the real page first and then has it
 *     covered up, which is worse than no splash at all. So the markup is
 *     server-rendered on every document and revealed by the `[data-app-shell]`
 *     attribute the pre-paint snippet in the root layout already applies.
 *
 *  2. IT MUST DISMISS ITSELF EVEN IF NO JAVASCRIPT EVER RUNS. A full-viewport
 *     overlay whose only exit is a React effect is one failed hydration away
 *     from a permanently unusable app. The exit is therefore a CSS animation
 *     with `forwards` fill: a broken bundle costs the user a fade, not the
 *     product. JavaScript only *observes* the handoff, it does not own it.
 *
 * The timings below are exported so the TypeScript side can schedule the intro
 * handoff against the same numbers the stylesheet animates with, instead of a
 * second set of magic numbers that would drift from `features/onboarding/splash.css`.
 */

/** How long the splash holds at full opacity before it begins to leave. */
export const SPLASH_HOLD_MS = 1500;

/** The fade itself. HOLD + FADE must equal the 1920ms in onboarding/splash.css. */
export const SPLASH_FADE_MS = 420;

/** Total lifetime of the overlay, from first paint to `visibility: hidden`. */
export const SPLASH_TOTAL_MS = SPLASH_HOLD_MS + SPLASH_FADE_MS;

/**
 * Where the first-run intro records that it has been seen.
 *
 * VERSIONED IN THE KEY ITSELF, and that is the point of the suffix: the intro
 * describes what the product does, so when the product changes enough that the
 * description is wrong, the honest fix is to show the new one — which a new
 * key does, to everyone, without any migration code. Bumping it is a deliberate
 * act; nothing bumps it automatically.
 */
export const ONBOARDING_SEEN_KEY = "nftbaz-onboarding-seen-v1";

/**
 * Whether the first-run intro should be shown.
 *
 * PURE, and it takes the two facts rather than reading them, so every branch is
 * assertable without a fake DOM or a fake storage.
 *
 * `storedSeen` is `null` for "nothing stored" AND for "storage threw", which
 * are deliberately the same answer here: a privacy-restricted web view that
 * cannot remember anything should still get the intro on the launch it is in.
 * The cost of that choice is bounded — it is four slides with a Skip control,
 * shown to a user who has already chosen to install the app.
 */
export function shouldShowIntro(isAppShell: boolean, storedSeen: string | null): boolean {
  // BROWSERS NEVER SEE IT. Not a taste call: a full-viewport takeover on a
  // crawled, indexable page is a cloaking-shaped interstitial, and the intro
  // describes installing and using *the app*. `useIsAppShell` reports false
  // until the verdict resolves and `app-shell.ts` fails safe toward `web`, so
  // an unresolved shell shows nothing — correct by default, in that order.
  if (!isAppShell) return false;
  return storedSeen !== "1";
}

/** Read the remembered verdict. Returns null where storage is unavailable. */
export function readOnboardingSeen(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(ONBOARDING_SEEN_KEY);
  } catch {
    return null;
  }
}

/** Remember that the intro has been completed or skipped. Never throws. */
export function markOnboardingSeen(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ONBOARDING_SEEN_KEY, "1");
  } catch {
    // Blocked storage. The user sees the intro again next launch, which is
    // the only failure mode available and is not a broken one.
  }
}

/**
 * Marks `<html>` once the launch sequence has finished in THIS document.
 *
 * WHY AN ATTRIBUTE AND NOT COMPONENT STATE. The splash is server-rendered
 * markup whose exit is a CSS animation, so anything that REMOUNTS its subtree
 * gives it a fresh element and the animation starts again — and `ClientLayout`
 * does exactly that when a navigation crosses between its fullscreen branch
 * (`/auth`, `/connect-wallet`) and its ordinary one. Without this the user
 * would get a full splash on the way into login, mid-session.
 *
 * A document-level attribute is the only thing with the right lifetime: it
 * outlives every component, it is readable by the stylesheet that owns the
 * animation, and it dies with the document — which is correct, because the
 * NEXT document genuinely is a new launch and should splash again.
 */
export const LAUNCH_DONE_ATTR = "data-app-launched";

export function hasLaunchCompleted(): boolean {
  if (typeof document === "undefined") return false;
  return document.documentElement.getAttribute(LAUNCH_DONE_ATTR) === "1";
}

export function markLaunchCompleted(): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute(LAUNCH_DONE_ATTR, "1");
}
